import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import type { Guide } from './types';

/**
 * „Co nowego” — lista przewodników po funkcjach, które użytkownik widzi
 * w swojej wersji aplikacji (przewodnik po zmianach, 21.10.2026). Przy
 * każdej: pozycja prezentacji, „Co to robi”, czy przewodnik był oglądany
 * i „Pokaż przewodnik”.
 */
export const WhatsNewDialog = ({
  guides,
  seen,
  onOpen,
  onClose,
}: {
  guides: readonly Guide[];
  seen: ReadonlySet<string>;
  onOpen: (flagKey: string) => void;
  onClose: () => void;
}) => (
  <Dialog
    open
    onClose={onClose}
    maxWidth="sm"
    fullWidth
    aria-labelledby="guide-whats-new-title"
    data-guide-overlay=""
    data-testid="guide-whats-new-dialog"
  >
    <DialogTitle id="guide-whats-new-title">Co nowego — przewodniki po zmianach</DialogTitle>
    <DialogContent dividers>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Funkcje czekające na decyzję, które widzisz w tej wersji aplikacji. Przewodnik pokazuje krok po kroku,
        co funkcja robi i co kliknąć — ze slajdami i podświetleniem na ekranie.
      </Typography>
      {guides.length === 0 ? (
        <Typography variant="body2" data-testid="guide-whats-new-empty">
          W tej wersji aplikacji nie ma nowych funkcji z przewodnikiem.
        </Typography>
      ) : (
        <Stack spacing={1.5}>
          {guides.map((g) => (
            <Box
              key={g.flagKey}
              data-testid={`guide-whats-new-item-${g.flagKey}`}
              sx={{ p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1 }}
            >
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }} useFlexGap>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, flex: '1 1 auto' }}>
                  {g.title}
                </Typography>
                <Chip
                  size="small"
                  color={seen.has(g.flagKey) ? 'default' : 'secondary'}
                  label={seen.has(g.flagKey) ? 'obejrzany' : 'nowe'}
                />
              </Stack>
              {/* Opis skracany do trzech wierszy — pełna treść w dymku (audyt układu 23.10.2026). */}
              <Typography
                variant="body2"
                color="text.secondary"
                title={g.whatItDoes}
                sx={{
                  mt: 0.5,
                  display: '-webkit-box',
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                {g.whatItDoes}
              </Typography>
              <Box sx={{ mt: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="caption" color="text.secondary">
                  Dla kogo: {g.audience.join(', ')} · kroków: {g.steps.length}
                </Typography>
                <Button size="small" onClick={() => onOpen(g.flagKey)} data-testid={`guide-open-${g.flagKey}`}>
                  Pokaż przewodnik
                </Button>
              </Box>
            </Box>
          ))}
        </Stack>
      )}
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose}>Zamknij</Button>
    </DialogActions>
  </Dialog>
);
